import { SiteDetailsSections } from "../../add-element/collapsed/CollapsedSiteDetails";
import { PageSlugs } from "../../add-element/types";
import { isUsingNewComponents } from "../../add-element/utils";
import {
  dashedBorderStyle as baseDashedBorderStyle,
  displayWebsiteContentStyles,
} from "../../add-element/website-display/DisplayWebsiteContent";
import { useIsPreviewOrDemo } from "../../contexts/IsPreviewOrDemoContext";
import {
  isNewLayoutsEnabled,
  useIsClickToEditEnabled,
} from "../../contexts/StaticSiteDataContext";
import { TrackContext, TrackContextValue } from "../../contexts/TrackContext";
import { useCurrentSite, useSites } from "../../contexts/models/SiteContext";
import { APIError } from "../../core/api";
import {
  RouteInfo,
  useBuildUrl,
  useRouteActive,
  useRouteInfo,
  useRouterNavigation,
} from "../../core/routing";
import { closeModal, openModal } from "../../modals/actions";
import { buildRouteArgs } from "../../pages/utils";
import Paragraphs from "../../pages/website/Paragraphs";
import {
  OnlineInvitationSite,
  Page,
  Site,
  ThemeIdentifier,
  WeddingSite,
} from "../../resources/modelTypes";
import { ReduxResourceContainer } from "../../resources/reduxTypes";
import EditButton from "../../shared/EditButton";
import { useScrollTo } from "../../shared/hooks/useScrollTo";
import { hashPath } from "../../track/utils";
import couplePhotoLargeDefault from "../images/website/photo-couple-wide.png";
import couplePhotoDefault from "../images/website/photo-couple.png";
import singlePhotoDefault from "../images/website/photo-single.png";
import { selectAsset } from "../selectors";
import AnnouncementBanner from "./AnnouncementBanner";
import { AssetSpec, assetSpecs } from "./EditLayout/mediaSpecs";
import FancyClothesContext from "./FancyClothesContext";
import NavigatePreviewContext from "./NavigatePreviewContext";
import SitePreviewContext, {
  FocusedClickSection,
  HoveredSection,
} from "./SitePreviewContext";
import { ModularWeddingHeader } from "./header/ModularWeddingHeader";
import { SwipePanelView } from "./online-invitation/mobile/SectionHeights";
import { css, SerializedStyles } from "@emotion/react";
import {
  LargeDivider,
  LargeGraphic,
  LargeHeadline,
  MobileLargeGraphic,
  WeddingHomeHorizontalFullHeader,
  WeddingHomeVerticalDoubleHeader,
  WeddingHomeVerticalHeader,
  WeddingHomeVerticalSingleHeader,
  withDesign,
} from "@minted/fancyclothes";
import {
  Alert,
  Icon,
  styleUtils,
  theme,
  useMediaQueryState,
} from "@minted/minted-components";
import { format as formatDate, parse as parseDate } from "date-fns";
import React, {
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import TopBarProgress from "react-topbar-progress-indicator";

const CONFIRMATION_MODAL = "CONFIRMATION_MODAL";
const HEADER_PHOTO_MANAGER_MODAL = "HEADER_PHOTO_MANAGER_MODAL";
const HTTP_TOO_MANY_REQUESTS = 429;
const MOBILE_BREAKPOINT = "xs";
const OVERLAY_Z_INDEX = 999;

type HeaderStylesType = {
  centered: SerializedStyles;
  dashedBorderStyle: SerializedStyles;
  editButtonCircle: SerializedStyles;
  editButtonContainer: SerializedStyles;
  editButtonOverlay: SerializedStyles;
  fullWidth: SerializedStyles;
  image: SerializedStyles;
  moderationErrorOverlay: SerializedStyles;
  pillButton: SerializedStyles;
  solidBorderStyle: SerializedStyles;
  wrapper: (isWedding: boolean) => SerializedStyles;
};

const headerStyles: HeaderStylesType = {
  centered: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x4};
    margin-bottom: ${styleUtils.rem(40)};
    margin-left: auto;
    margin-right: auto;
    max-width: ${styleUtils.rem(780)};
    padding-left: ${theme.spacing.x4};
    padding-right: ${theme.spacing.x4};
    text-align: center;
  `,
  dashedBorderStyle: css`
    & .coupleVerticalNames,
    & .introContentWrapper,
    & .weddingDateWrapper,
    & .coupleHorizontalNames {
      ${baseDashedBorderStyle};
    }
    & button {
      margin-bottom: ${theme.spacing.x2};
    }
    & .editButtonWrapper.l2 {
      margin-top: ${styleUtils.rem(30)};
    }
    &.l2,
    &.l3,
    &.l4 {
      & .editButtonWrapper {
        margin-top: ${styleUtils.rem(30)};
      }
      & .weddingDateWrapper {
        margin-top: 0 !important;
      }
    }
    & .coupleNamesWrapper.l3 {
      & .editButtonWrapper {
        margin-top: ${theme.spacing.x2};
      }
    }
  `,
  editButtonCircle: css`
    align-items: center;
    background-color: ${theme.colors.white};
    border-radius: 50%;
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
    color: ${theme.colors.gray700};
    cursor: pointer;
    display: flex;
    font-size: ${styleUtils.rem(25)};
    height: ${styleUtils.rem(50)};
    justify-content: center;
    width: ${styleUtils.rem(50)};
  `,
  editButtonContainer: css`
    position: relative;
  `,
  editButtonOverlay: css`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: ${styleUtils.rem(8)};
    position: absolute;
    right: ${styleUtils.rem(15)};
    top: ${styleUtils.rem(15)};
    z-index: ${OVERLAY_Z_INDEX};
  `,
  moderationErrorOverlay: css`
    bottom: ${theme.spacing.x2};
    left: ${theme.spacing.x2};
    position: absolute;
    right: ${theme.spacing.x2};
    z-index: ${OVERLAY_Z_INDEX};
  `,
  fullWidth: css`
    align-self: center;
    width: 15%;
  `,
  image: css`
    box-sizing: border-box;
    display: block;
    margin-bottom: ${styleUtils.rem(30)};
    max-height: ${styleUtils.rem(650)};
    object-fit: cover;
    width: 100%;
  `,
  pillButton: css`
    display: flex;
    justify-content: center;
    margin-bottom: ${theme.spacing.x2};
  `,
  solidBorderStyle: css`
    & .coupleVerticalNames,
    & .introContentWrapper,
    & .weddingDateWrapper,
    &.coupleNamesWrapper.l3 {
      ${displayWebsiteContentStyles.focusedSectionSolid};
    }
    &.l2 {
      & .weddingDateWrapper {
        margin-top: ${styleUtils.rem(30)};
      }
    }
  `,
  wrapper: (isWedding: boolean) => css`
    margin-bottom: ${styleUtils.rem(isWedding ? 80 : 40)};
  `,
};

/**
 * Known default pages that have explicit route definitions
 */
const KNOWN_DEFAULT_PAGES = new Set<PageSlugs>([
  PageSlugs.Accommodations,
  PageSlugs.Events,
  PageSlugs.FAQ,
  PageSlugs.GuestBook,
  PageSlugs.Home,
  PageSlugs.Photos,
  PageSlugs.Registry,
  PageSlugs.RSVP,
  PageSlugs.WeddingParty,
]);

type SectionWrapperPropsParams = {
  focusedSectionId: number | SiteDetailsSections | null | undefined;
  hoveredSectionId: number | SiteDetailsSections | null | undefined;
  isClickable: boolean;
  isMobile: boolean;
  onSectionEditClick: (sectionId: SiteDetailsSections) => void;
  sectionId: SiteDetailsSections;
  setFocusedClickSection: (section: FocusedClickSection | null) => void;
  setHoveredSection: (section: HoveredSection | null) => void;
  themeIdentifier: ThemeIdentifier;
  wrapperClassName: string;
};

type SectionWrapperProps = {
  className: string;
  css: SerializedStyles | undefined;
  onClick: (() => void) | undefined;
  onMouseEnter: (() => void) | undefined;
  onMouseLeave: (() => void) | undefined;
  topComponent: ReactNode;
};

const getSectionWrapperProps = ({
  focusedSectionId,
  hoveredSectionId,
  isClickable,
  isMobile,
  onSectionEditClick,
  sectionId,
  setFocusedClickSection,
  setHoveredSection,
  themeIdentifier,
  wrapperClassName,
}: SectionWrapperPropsParams): SectionWrapperProps => ({
  className: `${wrapperClassName} ${themeIdentifier}`,
  css:
    focusedSectionId === sectionId
      ? headerStyles.solidBorderStyle
      : isClickable && (hoveredSectionId === sectionId || isMobile)
        ? headerStyles.dashedBorderStyle
        : undefined,
  onClick: isClickable
    ? () => {
        onSectionEditClick(sectionId);
        setFocusedClickSection({
          id: sectionId,
          isSolidBorder: true,
          type: "siteDetails",
        });
      }
    : undefined,
  onMouseEnter: isClickable
    ? () => setHoveredSection({ id: sectionId, type: "siteDetails" })
    : undefined,
  onMouseLeave: isClickable ? () => setHoveredSection(null) : undefined,
  topComponent: (
    <EditButton
      isClickable={isClickable}
      isMobile={isMobile}
      sectionId={sectionId}
      themeIdentifier={themeIdentifier}
    />
  ),
});

type NavItem = {
  active: boolean;
  onClick: (event: React.MouseEvent) => void;
  title: string;
  url: string;
};

const buildNavItems = (
  pages: Page[],
  buildUrl: (routeName: string, routeParams?: Record<string, string>) => string,
  navigateTo: (
    routeName: string,
    routeParams?: Record<string, string>,
  ) => void | null,
  isCustomizePage: boolean,
  usePreviewOverride: boolean,
  emitSiteEvent: TrackContextValue,
  scrollToId: (id: string) => void,
  scrollToTop: () => void,
  setPreviewOverrideSlug: ((slug: string | null) => void) | undefined,
  site: Site,
  isMobile: boolean,
  isMultiPage: boolean,
  isRouteActiveHook: (
    routeName: string,
    params?: Record<string, string>,
    strictEquality?: boolean,
  ) => boolean,
  currentRoute: RouteInfo,
  isDemo: boolean,
  isNewLayoutsEnabledFlag: boolean,
  pendingScrollSlugRef: React.MutableRefObject<string | null>,
): NavItem[] => {
  const referrer = document.referrer;

  return pages.map((page) => {
    // Use different route logic based on context:
    // - Dashboard (isCustomizePage): use nested routes like "pages.custom"
    // - Guest website: use flat routes like "testing-routing"
    let routeName: string;
    let routeParams: Record<string, string>;

    if (isCustomizePage) {
      const routeArgs = buildRouteArgs({ data: page });

      routeName = routeArgs.name;
      routeParams = routeArgs.params as Record<string, string>;
    } else {
      // On guest website, routes are flat and just use the slug
      routeName = page.slug;
      routeParams = {};
    }

    // Check if this page's route is active
    let active;

    if (isCustomizePage) {
      // Special handling for customize page routing
      if (page.slug === PageSlugs.Home) {
        active =
          isRouteActiveHook("pages", {}, true) ||
          isRouteActiveHook("pages.home");
      } else if (KNOWN_DEFAULT_PAGES.has(page.slug)) {
        // For known default pages, check pages.{slug}
        active = isRouteActiveHook(`pages.${page.slug}`);
      } else {
        // For custom pages, check if slug is in the current path
        active = currentRoute.path.includes(`/${page.slug}`);
      }
    } else {
      // For guest website, check the flat route
      active = isRouteActiveHook(page.slug, {}, true);
    }

    return {
      active,
      onClick: (event: React.MouseEvent) => {
        event.preventDefault();
        emitSiteEvent({
          event: "page_load",
          feature: isDemo ? "site_demo" : "site_guest_experience",
          guest_id: localStorage.getItem("mintedWebsitesGuestId"),
          is_mobile: isMobile,
          referrer_domain: referrer.split("?")[0],
          referrer_hash: hashPath(referrer),
          tab_name: page.givenName,
          tab_slug: page.slug,
          url_hash: hashPath(window.location.href),
        });

        if (usePreviewOverride) {
          setPreviewOverrideSlug?.(page.slug);
        } else {
          if (isMultiPage) {
            if (isNewLayoutsEnabledFlag && page.slug !== PageSlugs.Home) {
              pendingScrollSlugRef.current = page.slug;
            } else {
              scrollToTop();
            }
          } else {
            scrollToId(`page-${page.slug}`);
          }

          if (navigateTo) {
            navigateTo(routeName, routeParams);
          }
        }
      },
      title: page.givenName,
      url: buildUrl(routeName, routeParams),
    };
  });
};

type Assets = {
  brideImage?: string | null;
  groomImage?: string | null;
  themeImage?: string | null;
};

type HeaderContentProps = {
  assets: Assets;
  breakpoint?: string;
  children?: ReactNode;
  emitSiteEvent: TrackContextValue;
  focusedClickSection: FocusedClickSection | null;
  hoveredSection: HoveredSection | null;
  isClickToEditEnabled: boolean;
  isMobile: boolean;
  isPreview: boolean;
  isSecondaryPage: boolean;
  navItems: NavItem[];
  onSectionEditClick: (idOfResource: SiteDetailsSections) => void;
  setFocusedClickSection: (section: FocusedClickSection | null) => void;
  setHoveredSection: (section: HoveredSection | null) => void;
  site: Site;
  textAlignment: string;
};

const HeaderContentComponent = ({
  assets,
  breakpoint,
  children,
  emitSiteEvent,
  focusedClickSection,
  hoveredSection,
  isClickToEditEnabled,
  isMobile,
  isPreview,
  isSecondaryPage,
  navItems,
  onSectionEditClick,
  setFocusedClickSection,
  setHoveredSection,
  site,
  textAlignment,
}: HeaderContentProps): React.ReactElement | null => {
  const dispatch = useDispatch();
  const { setSiteAsset } = useSites();
  const [assetTransitioning, setAssetTransitioning] = useState(false);
  const [moderationError, setModerationError] = useState(false);

  const onEditOIMedia = useCallback(
    (assetSpec: AssetSpec) => {
      emitSiteEvent({ event: "oi_upload_design_edit_clicked" });
      dispatch(
        openModal(HEADER_PHOTO_MANAGER_MODAL, {
          cropAspectRatio: assetSpec.width / assetSpec.height,
          minSize: { height: assetSpec.height, width: assetSpec.width },
          onSave: (selectedImage: string) => {
            setAssetTransitioning(true);
            setModerationError(false);
            setSiteAsset(assetSpec.key, selectedImage)
              .then(() => {
                emitSiteEvent({ event: "oi_upload_design_uploaded" });
              })
              .catch((error: unknown) => {
                if (
                  error instanceof APIError &&
                  error.response?.status === HTTP_TOO_MANY_REQUESTS
                ) {
                  setModerationError(true);
                }
              })
              .finally(() => setAssetTransitioning(false));
          },
        }),
      );
    },
    [dispatch, emitSiteEvent, setSiteAsset],
  );

  const onResetOIMedia = useCallback(
    (assetSpec: AssetSpec) => {
      dispatch(
        openModal(CONFIRMATION_MODAL, {
          children:
            "This will remove your uploaded image and restore the original theme image.",
          onSubmit: () => {
            setAssetTransitioning(true);
            setSiteAsset(assetSpec.key, null)
              .then(() => {
                emitSiteEvent({ event: "oi_upload_design_cleared" });
              })
              .finally(() => {
                setAssetTransitioning(false);
                dispatch(closeModal());
              });
          },
          open: true,
          showCancel: true,
          submitButtonText: "Use original image",
        }),
      );
    },
    [dispatch, emitSiteEvent, setSiteAsset],
  );

  if (site.isWedding) {
    const siteDetails = (site as WeddingSite).siteDetails;
    // The legacy themeIdentifier headers collapse names, media, the wedding date
    // and Our Story into one condensed treatment on secondary pages. The modular
    // header keeps all three elements everywhere and gates only the last two, so
    // it takes isSecondaryPage directly.
    const condensed = !isNewLayoutsEnabled(site) && isSecondaryPage;
    let weddingDate: string;
    const eventDateHeading =
      siteDetails.eventDateHeading ||
      "Please join us for our wedding celebration on";

    if (siteDetails.weddingDate) {
      // change format to what fancyclothes requires
      try {
        const parsedDate = parseDate(
          siteDetails.weddingDate,
          "MM/dd/yyyy",
          new Date(),
        ); // Using new Date(site.weddingDate) was off by one day

        weddingDate = formatDate(parsedDate, "yyyy-MM-dd");
      } catch {
        weddingDate = `${new Date().getFullYear() + 1}-08-01`;
      }
    } else {
      weddingDate = `${new Date().getFullYear() + 1}-08-01`;
    }
    const commonProps = {
      bottomName: siteDetails.spouseName || "Name",
      condensed,
      countdownText: siteDetails.countdownText || "the big day",
      eventDateHeading,
      introHeading: siteDetails?.isOurStoryVisible
        ? siteDetails.welcomeHeading || "Our Story"
        : null,
      isCountdownVisible: siteDetails.isCountdownVisible,
      isOurStoryVisible: siteDetails?.isOurStoryVisible,
      isWeddingDateVisible:
        siteDetails.isWeddingDateVisible && Boolean(siteDetails.weddingDate),
      leftName: siteDetails.yourName || "Name",
      nameSeparator: siteDetails.nameSeparator,
      navItems,
      rightName: siteDetails.spouseName || "Name",
      textAlignment,
      topName: siteDetails.yourName || "Name",
      weddingDate,
    };

    const isPreviewSectionClickable =
      isPreview && !condensed && isClickToEditEnabled;
    const commonSectionProps = {
      focusedSectionId: focusedClickSection?.id,
      hoveredSectionId: hoveredSection?.id,
      isClickable: isPreviewSectionClickable,
      isMobile,
      onSectionEditClick,
      setFocusedClickSection,
      setHoveredSection,
      themeIdentifier: site.themeIdentifier,
    };

    const sectionsProps = {
      coupleNamesProps: getSectionWrapperProps({
        sectionId: SiteDetailsSections.YourNames,
        wrapperClassName: "coupleNamesWrapper",
        ...commonSectionProps,
      }),
      eventDetailsWrapperProps: getSectionWrapperProps({
        sectionId: SiteDetailsSections.TheBigDay,
        wrapperClassName: "eventDetailsWrapper",
        ...commonSectionProps,
      }),
      ourStoryWrapperProps: getSectionWrapperProps({
        sectionId: SiteDetailsSections.WelcomeMessage,
        wrapperClassName: "welcomeMessageWrapper",
        ...commonSectionProps,
      }),
    };

    if (isNewLayoutsEnabled(site)) {
      return (
        <ModularWeddingHeader
          fallbackFirstPhotoURL={assets.brideImage}
          fallbackPhotoURL={assets.themeImage || couplePhotoDefault}
          fallbackSecondPhotoURL={assets.groomImage}
          headerImages={(site as WeddingSite).headerImages ?? []}
          isSecondaryPage={isSecondaryPage}
          navItems={navItems}
          siteDetails={siteDetails}
          textAlignment={textAlignment}
          weddingDate={weddingDate}
          {...sectionsProps}
        >
          {children}
        </ModularWeddingHeader>
      );
    }

    switch (site.themeIdentifier) {
      case "l1":
        return (
          <WeddingHomeVerticalSingleHeader
            photoURL={assets.themeImage || couplePhotoDefault}
            {...commonProps}
            {...sectionsProps}
          >
            {children}
          </WeddingHomeVerticalSingleHeader>
        );
      case "l2":
        return (
          <WeddingHomeVerticalDoubleHeader
            leftPhotoURL={assets.brideImage || singlePhotoDefault}
            rightPhotoURL={assets.groomImage || singlePhotoDefault}
            {...commonProps}
            eventDateHeading={eventDateHeading + " "} // Needs space for inline heading + date
            {...sectionsProps}
          >
            {children}
          </WeddingHomeVerticalDoubleHeader>
        );
      case "l3":
        return (
          <WeddingHomeHorizontalFullHeader
            photoURL={assets.themeImage || couplePhotoLargeDefault}
            {...commonProps}
            {...sectionsProps}
          >
            {children}
          </WeddingHomeHorizontalFullHeader>
        );
      case "l4":
        return (
          <WeddingHomeVerticalHeader {...commonProps} {...sectionsProps}>
            {children}
          </WeddingHomeVerticalHeader>
        );
    }
  } else if (site.isOnlineInvitation) {
    const isMobileInPreview = isPreview && breakpoint === MOBILE_BREAKPOINT;
    const siteDetails = (site as OnlineInvitationSite).siteDetails;

    return (
      <div>
        {assetTransitioning && <TopBarProgress />}
        <div css={headerStyles.editButtonContainer} data-cy="mainImage">
          {assets.themeImage ? (
            <img
              alt={siteDetails.eventHeading || ""}
              css={headerStyles.image}
              src={assets.themeImage}
            />
          ) : isMobile || isMobileInPreview ? (
            <MobileLargeGraphic text="Online invitation header image" />
          ) : (
            <LargeGraphic text="Online invitation header image" />
          )}
          {isPreview && (
            <div css={headerStyles.editButtonOverlay}>
              <div
                css={headerStyles.editButtonCircle}
                onClick={() => onEditOIMedia(assetSpecs.l1.themeImage)}
              >
                <Icon type={Icon.types.editSquare} />
              </div>
              {assets.themeImage && (
                <div
                  css={headerStyles.editButtonCircle}
                  onClick={() => onResetOIMedia(assetSpecs.l1.themeImage)}
                >
                  <Icon type={Icon.types.close} />
                </div>
              )}
            </div>
          )}
          {moderationError && (
            <div css={headerStyles.moderationErrorOverlay}>
              <Alert
                primaryText="Your image could not be uploaded due to moderation policies. Please reach out to customer service if you think this was a mistake."
                secondaryText=""
                show
                type={Alert.types.negative}
              />
            </div>
          )}
        </div>
        {!isUsingNewComponents(site) && (
          <div css={headerStyles.centered}>
            <LargeHeadline>{siteDetails.eventHeading || ""}</LargeHeadline>
            <div css={headerStyles.fullWidth}>
              <hr />
            </div>
            <Paragraphs
              text={siteDetails.eventDescription || ""}
              textAlignment="center"
            />
          </div>
        )}
      </div>
    );
  }

  return null;
};

const HeaderContent = memo(HeaderContentComponent);

type HeaderDividerComponentProps = {
  getRule: (ruleName: string, componentName: string) => string | undefined;
  isMultiPage: boolean;
  marginTop?: number;
};

class HeaderDividerComponent extends React.Component<HeaderDividerComponentProps> {
  render() {
    const { getRule, isMultiPage, marginTop = 0 } = this.props;
    const introBackgroundColor = getRule("backgroundColor", "IntroContainer");
    const introBackgroundPattern = getRule(
      "backgroundPattern",
      "IntroContainer",
    );
    const hasNoIntroBackground = !(
      introBackgroundColor || introBackgroundPattern
    );
    const shouldHaveSeparator = isMultiPage || hasNoIntroBackground;

    const dividerStyles = css`
      margin-top: ${styleUtils.rem(marginTop)};
    `;

    return (
      shouldHaveSeparator && (
        <div css={dividerStyles}>
          <LargeDivider />
        </div>
      )
    );
  }
}

const HeaderDivider = withDesign("HeaderDivider")(HeaderDividerComponent);

type WebsiteHeaderOwnProps = {
  breakpoint?: string;
  children?: ReactNode;
  isCustomizePage?: boolean;
  isSecondaryPage?: boolean;
  pages: Page[];
  textAlignment?: string;
  usePreviewOverride?: boolean;
};

const WebsiteHeader = ({
  breakpoint,
  children,
  isCustomizePage = false,
  isSecondaryPage = false,
  pages,
  textAlignment = "center",
  usePreviewOverride = false,
}: WebsiteHeaderOwnProps): React.ReactElement => {
  const site = useCurrentSite() as WeddingSite;
  const resources = useSelector(
    (state: { resources: ReduxResourceContainer }) => state.resources,
  );

  const assets: Assets = useMemo(() => {
    const computedAssets: Assets = {};
    switch (site.themeIdentifier) {
      case "l1":
        computedAssets.themeImage = selectAsset(
          resources,
          "themeImage",
          "crop1200650",
          site,
        );
        break;
      case "l2":
        computedAssets.brideImage = selectAsset(
          resources,
          "brideImage",
          "crop500500",
          site,
        );
        computedAssets.groomImage = selectAsset(
          resources,
          "groomImage",
          "crop500500",
          site,
        );
        break;
      case "l3":
        computedAssets.themeImage = selectAsset(
          resources,
          "themeImage",
          "crop16001067",
          site,
        );
        break;
      case "l4":
        break; // no images
    }
    if (site.isWedding && isNewLayoutsEnabled(site)) {
      computedAssets.brideImage ??= selectAsset(
        resources,
        "brideImage",
        "crop500500",
        site,
      );
      computedAssets.groomImage ??= selectAsset(
        resources,
        "groomImage",
        "crop500500",
        site,
      );
    }
    return computedAssets;
  }, [site, resources]);
  const { isDemo, isPreview } = useIsPreviewOrDemo();
  const {
    focusedClickSection,
    hoveredSection,
    onSectionEditClick,
    setFocusedClickSection,
    setHoveredSection,
  } = useContext(SitePreviewContext);
  const buildUrl = useBuildUrl();
  const routerNavigateTo = useRouterNavigation();
  const navigatePreviewContext = useContext(NavigatePreviewContext);
  const navigateTo = navigatePreviewContext?.navigateTo ?? routerNavigateTo;
  const route = useRouteInfo();
  const isRouteActiveCheck = useRouteActive();
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "small",
  });
  const isMobile = mediumMediaQueryState === "BELOW";
  const emitSiteEvent = useContext(TrackContext);
  const setPreviewOverrideSlug = navigatePreviewContext?.setPreviewOverrideSlug;
  const { scrollToId, scrollToTop } = useScrollTo();
  const isMultiPage = site.pageConfiguration === "multi_page";
  const isClickToEditEnabled = useIsClickToEditEnabled();
  const pendingScrollSlugRef = useRef<string | null>(null);

  const navItems = buildNavItems(
    pages,
    buildUrl,
    navigateTo,
    isCustomizePage,
    usePreviewOverride,
    emitSiteEvent,
    scrollToId,
    scrollToTop,
    setPreviewOverrideSlug,
    site,
    isMobile,
    isMultiPage,
    isRouteActiveCheck,
    route,
    isDemo,
    isNewLayoutsEnabled(site),
    pendingScrollSlugRef,
  );

  // Auto-scroll to the top of the requested content after navigating
  useEffect(() => {
    const slug = pendingScrollSlugRef.current;

    if (!slug) {
      return;
    }

    pendingScrollSlugRef.current = null;

    const navbar = document.querySelector(".navbarContainer");

    scrollToId(`page-${slug}`, {
      offset: navbar?.getBoundingClientRect().height ?? 0,
    });
  }, [route.name, scrollToId]);

  const isHomePage = ["pages.home", "home"].includes(route.name);

  const content = (
    <div
      css={!isUsingNewComponents(site) && headerStyles.wrapper(site.isWedding)}
    >
      {site.siteDetails.showAnnouncement && (
        <AnnouncementBanner
          bodyText={site.siteDetails.announcementBody ?? ""}
          headingText={site.siteDetails.announcementHeading ?? ""}
        />
      )}

      <FancyClothesContext.Consumer>
        {() => (
          <div>
            <HeaderContent
              assets={assets}
              breakpoint={breakpoint}
              emitSiteEvent={emitSiteEvent}
              focusedClickSection={focusedClickSection}
              hoveredSection={hoveredSection}
              isClickToEditEnabled={isClickToEditEnabled}
              isMobile={isMobile}
              isPreview={isPreview}
              isSecondaryPage={isSecondaryPage}
              navItems={navItems}
              onSectionEditClick={(idOfResource: SiteDetailsSections) => {
                onSectionEditClick(
                  idOfResource,
                  "siteDetails",
                  SwipePanelView.EventNameDescription,
                );
                navigateTo("pages.home");
              }}
              setFocusedClickSection={setFocusedClickSection}
              setHoveredSection={setHoveredSection}
              site={site}
              textAlignment={textAlignment}
            >
              {children}
            </HeaderContent>
            {!isUsingNewComponents(site) && (
              <HeaderDivider
                isMultiPage={isMultiPage}
                // WW-5106 TODO: change spacing using 'gap' instead of 'margin-bottom' inside the fancyclothes Wedding Headers, then remove this
                marginTop={
                  site.isWedding &&
                  !site.siteDetails.isOurStoryVisible &&
                  isHomePage &&
                  isMultiPage
                    ? 80
                    : 0
                }
              />
            )}
          </div>
        )}
      </FancyClothesContext.Consumer>
    </div>
  );

  return content;
};

const MemoizedWebsiteHeader = memo(WebsiteHeader);

export default MemoizedWebsiteHeader;
